import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import {
  Button as ShadcnButton,
  Card,
  CardContent,
  Alert as UiAlert,
  AlertDescription,
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@cargomatic/ui';
import CargomaticCollapsibleCard from 'components/v2/CollapsibleCard';
import { AlertCircle, ChevronDown, MapPin, EyeOff } from 'lucide-react';
import AuditHistory from 'components/modules/shipments/AuditHistory';
import EditReferenceNumbersModal from 'components/modules/shipments/EditReferenceNumbersModal';
import DataPoller from 'components/ui/DataPoller';
import ArrivedModal from 'components/ui/tailwind/modals/ArrivedModal';
import OrderShipmentsStore from 'stores/OrderShipmentsStore';
import { withTranslation } from 'react-i18next';
import OrdersActionCreators from '../../../actions/OrdersActionCreators';
import OrdersStore from '../../../stores/OrdersStore';
import SessionStore from '../../../stores/SessionStore';
import StatusBar from './StatusBar';
import OrderMap from './OrderMap';
import { OrderDocuments } from './OrderDocuments';
import CommentList from '../comments/CommentList';
import InboundDetails from './OrderDetails';
import ReferenceNumbers from './ReferenceNumbers';
import { OrderShipments } from './leg-management';
import ShipmentsList from './ShipmentsList';
import EquipmentsList from './EquipmentsList';
import AccountsPayable from './AccountsPayable';
import AccountsReceivable from './AccountsReceivable';
import AccessorialsList from './AccessorialsList';
import BulkAddAccessorialsModal from '../shipments/accounting/BulkAddAccessorialsModal';
import OrderRtbSummary from 'components/modules/orders/order-rtb/OrderRtbSummary';

import { isDrayageShipment } from '../../../helpers/shipment-types';
import { isLegManagementEnabled } from '../../../helpers/legs-helper';
import HazmatDetails from './HazmatDetailsList';

const POLLING_FREQ = 30000;

const shipmentQueryOptions = {
  paginate: true,
  per_page: 25,
  page: 1,
};

class Module extends Component {
  constructor(props) {
    super(props);

    const hasFinanceRole = SessionStore.hasFinanceRole();
    this.state = {
      order: null,
      title: '',
      shipmentsWithZeroValueAccessorials: [],
      audits: null,
      auditSearch: '',
      auditPage: 1,
      showEditReferenceNumbersModal: false,
      showArrivedModal: false,
      showBulkAddAccessorialsModal: false,
      showMap: true,
      hasFinanceRole,
      stagingShipments: [],
      bookedShipments: [],
      shipmentListPagination: {},
    };
  }

  componentDidMount() {
    OrdersActionCreators.getOrderShipments(
      this.props.id || this.props.match.params.id,
      shipmentQueryOptions,
    );
    OrderShipmentsStore.addChangeListener(this.handleOrderShipmentsStoreChange);
    OrdersStore.addStagingShipmentsChangeListener(
      this.handleStagingShipmentsChange,
    );
    OrdersStore.addChangeListener(this.handleOrdersStoreChange);
    SessionStore.addChangeListener(this.handleSessionStoreChange);
    this.handleStagingShipmentsChange();
  }

  componentWillUnmount() {
    OrdersStore.removeChangeListener(this.handleOrdersStoreChange);
    SessionStore.removeChangeListener(this.handleSessionStoreChange);
    OrdersActionCreators.resetStagingShipments();
    OrdersActionCreators.resetOrderShipments();
  }

  getOrder() {
    const { order } = this.state;
    return order || {};
  }

  getShipments() {
    return this.state.bookedShipments || [];
  }

  getShipmentsWithZeroValueAccessorials(order) {
    if (!order) {
      return [];
    }
    const orderShipments = this.state.bookedShipments || [];
    return orderShipments.filter((job) => {
      const receivables = job.receivables || [];
      const payables = job.payables || [];

      const zeroCostAR = receivables.filter((s) => s.value === 0);
      const zeroCostAP = payables.filter((s) => s.value === 0);
      return zeroCostAR.length > 0 || zeroCostAP > 0;
    });
  }

  handleOrderShipmentsStoreChange = () => {
    const orderShipments = OrderShipmentsStore.getOrderShipments();
    this.updateShipmentsState(orderShipments);
  };

  handleOrdersStoreChange = () => {
    const order = OrdersStore.getOrder();
    const audits = OrdersStore.getAudits();
    if (!this.state.title && order && order.order_id) {
      document.title = order.order_id;
      this.setState({ title: order.order_id });
    }

    const shipmentsWithZeroValueAccessorials =
      this.getShipmentsWithZeroValueAccessorials(order);
    this.setState({
      order,
      shipmentsWithZeroValueAccessorials,
      hasZeroValueAccessorials: shipmentsWithZeroValueAccessorials.length > 0,
      audits,
      auditLoading: false,
    });
  };

  fetchOrderAudits = () => {
    this.setState({ auditLoading: true });
    const id = this.props.id || this.props.match.params.id;
    OrdersActionCreators.getOrderAudits(id, {
      search: this.state.auditSearch || undefined,
      page: String(this.state.auditPage),
      perPage: '10',
    });
  };

  handleStagingShipmentsChange = () => {
    const stagingShipments = OrdersStore.getStagingShipments();
    if (!stagingShipments) {
      return;
    }
    this.setState({ stagingShipments });
  };

  handleCommentAdd = (comment) => {
    const {
      order: { _id: orderId },
    } = this.state;

    OrdersActionCreators.addComment(orderId, comment);
  };

  handleAddBulkAccessorialsClick = () =>
    this.setState({ showBulkAddAccessorialsModal: true });

  handleSessionStoreChange = () => {
    const hasFinanceRole = SessionStore.hasFinanceRole();
    this.setState({
      hasFinanceRole,
    });
  };

  updateReferenceNumbers(references) {
    OrdersActionCreators.updateOrder(this.state.order._id, {
      reference_numbers: references,
    });
    this.setState({
      showEditReferenceNumbersModal: false,
    });
  }

  updateOrderHazmatDetails(orderId, hazmatDetails) {
    OrdersActionCreators.updateOrder(orderId, {
      hazmat_details: hazmatDetails,
    });
  }

  renderMissingDocumentation() {
    const drayageShipmentsMissingDocumentation = this.getShipments().filter(
      (s) => isDrayageShipment(s) && s.missing_docs,
    );

    if (
      this.state.order &&
      !this.state.order.missing_docs &&
      drayageShipmentsMissingDocumentation.length === 0
    )
      return null;
    const { t } = this.props;
    return (
      <UiAlert
        variant="destructive"
        className="mb-4"
        data-testid="Alert-564a51e7"
      >
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{t('MISSING_DOC_FOR_ORDER')}</AlertDescription>
      </UiAlert>
    );
  }

  renderOutstandingARStatus() {
    const {
      order: { outstanding_ar, outstanding_ap },
    } = this.state;
    if (!outstanding_ar && !outstanding_ap) {
      return null;
    }
    const { t } = this.props;
    return (
      <UiAlert className="mb-4" data-testid="Alert-29539371">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {t('OUTSTANDING_BALANCE_FOR_ORDER')}
        </AlertDescription>
      </UiAlert>
    );
  }

  renderZeroBalanceNotification() {
    if (!this.state.hasZeroValueAccessorials) {
      return null;
    }

    const { shipmentsWithZeroValueAccessorials: jobs } = this.state;
    const { t } = this.props;
    return (
      <Collapsible defaultOpen className="mb-4" data-testid="Alert-8cebe271">
        <UiAlert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between w-full">
            <span>{t('NO_ACCESSORIAL_RATE_OR_COST_FOR_SHIPMENT')}</span>
            <CollapsibleTrigger className="ml-2 hover:bg-muted rounded p-1">
              <ChevronDown className="h-4 w-4" />
            </CollapsibleTrigger>
          </AlertDescription>
        </UiAlert>
        <CollapsibleContent>
          <div className="border border-t-0 rounded-b-lg px-4 py-2 space-y-1">
            {jobs.map((job) => {
              const jobLink = `/shipments/${job._id}`;
              return (
                <a
                  key={job._id}
                  href={jobLink}
                  className="block text-sm text-primary hover:underline"
                >
                  {job.shipment_id}
                </a>
              );
            })}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  getShipmentsEligibleForAccessorialsManagement() {
    return this.getShipments().filter((s) => {
      return !_.isEmpty(s.carrier);
    });
  }

  updateShipmentsState(bookedShipments) {
    this.setState((prevState) => {
      const { stagingShipments, shipmentListPagination: currentPagination } =
        prevState;

      const shipments = [...stagingShipments, ...bookedShipments];

      const pagination = OrderShipmentsStore.getOrderShipmentPaginationData();
      const shipmentListPagination = {
        totalPages: pagination.pages,
        currentPage: currentPagination.currentPage || 1,
      };
      return {
        bookedShipments,
        shipments,
        shipmentListPagination,
      };
    });
  }

  onGetOrderShipments = () => {
    const orderId = this.props.id || this.props.match.params.id;
    const options = {
      ...shipmentQueryOptions,
      page: this.state.shipmentListPagination.currentPage || 1,
    };
    OrdersActionCreators.getOrderShipments(orderId, options);
  };

  handleShipmentListPagination = (eventKey) => {
    const paginationData = OrderShipmentsStore.getOrderShipmentPaginationData();
    this.setState(
      {
        shipmentListPagination: {
          currentPage: eventKey,
          totalPages: paginationData.pages,
        },
      },
      this.onGetOrderShipments,
    );
  };

  render() {
    const { order, bookedShipments, stagingShipments } = this.state;
    const id = this.props.id || this.props.match.params.id;
    const { hideMap, renderedInModal, t } = this.props;

    return (
      <div data-testid="div-95433961" className="px-4 sm:px-6">
        <DataPoller
          dataTestId="DataPoller-143d43d3"
          action={() => OrdersActionCreators.getOrder(id)}
          frequency={POLLING_FREQ}
        />
        <DataPoller
          dataTestId="DataPoller-cd654e78"
          action={() =>
            OrdersActionCreators.getOrderAudits(id, {
              search: this.state.auditSearch || undefined,
              page: String(this.state.auditPage),
              perPage: '10',
            })
          }
          frequency={POLLING_FREQ}
        />
        <DataPoller
          dataTestId="DataPoller-af62a7e5"
          action={this.onGetOrderShipments}
          frequency={POLLING_FREQ}
        />
        {order && order._id && (
          <div data-testid="div-889d6180" id="order-details-container">
            {this.renderMissingDocumentation()}
            {this.renderOutstandingARStatus()}
            {this.renderZeroBalanceNotification()}
            <Card>
              <CardContent className="p-0">
                <StatusBar
                  order={order}
                  shipments={bookedShipments}
                  onArrivedClick={() =>
                    this.setState({ showArrivedModal: true })
                  }
                />
                {!hideMap && (
                  <div>
                    <div className="flex justify-end px-4 py-2">
                      <ShadcnButton
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          this.setState({
                            showMap: !this.state.showMap,
                          })
                        }
                      >
                        {this.state.showMap ? (
                          <EyeOff className="w-4 h-4 mr-1" />
                        ) : (
                          <MapPin className="w-4 h-4 mr-1" />
                        )}
                        {this.state.showMap ? 'Hide Map' : 'Show Map'}
                      </ShadcnButton>
                    </div>
                    {this.state.showMap && (
                      <div data-testid="div-9c2d5406">
                        <OrderMap
                          arriving={order.arriving}
                          delivering={order.delivering}
                        />
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            <div
              data-testid="div-f11b63c0"
              className="mt-5 grid gap-y-6 lg:grid-cols-12 lg:gap-6"
            >
              <div data-testid="div-9f2bf684" className="lg:col-span-4 min-w-0">
                <InboundDetails
                  order={order}
                  onEdit={(edit) => {
                    OrdersActionCreators.updateOrder(order._id, edit);
                  }}
                />
              </div>
              <div data-testid="div-f6319b88" className="lg:col-span-4 min-w-0">
                <ReferenceNumbers
                  order={order}
                  onEdit={() => {
                    this.setState({ showEditReferenceNumbersModal: true });
                  }}
                />
              </div>
              <div
                data-testid="div-d2d86e6e"
                className="lg:col-span-4 space-y-6 min-w-0"
              >
                <OrderDocuments order={order} shipments={bookedShipments} />
                <CargomaticCollapsibleCard
                  id="order-notes"
                  header={() => <span>{t('NOTE', { count: 2 })}</span>}
                >
                  <CommentList
                    comments={order.comments}
                    onCommentAdd={this.handleCommentAdd}
                  />
                </CargomaticCollapsibleCard>
              </div>
            </div>
            <div data-testid="div-a7e55760" className="my-6">
              <HazmatDetails
                order={order}
                updateOrderHazmatDetails={this.updateOrderHazmatDetails}
                isEditable={true}
              />
            </div>
            <div data-testid="div-a7e55759" className="my-6">
              <EquipmentsList order={order} shipments={bookedShipments} />
            </div>
            {isLegManagementEnabled(order) && (
              <OrderShipments orderId={id} order={order} />
            )}
            {/* Temp hide due to CARGO-9056 */}
            {/* {SessionStore.isAdmin() && order.order_type !== 'export' && ( */}
            {/*  <div className="my-6"> */}
            {/*    <PortIntegration isShipment={false} data={order} /> */}
            {/*  </div> */}
            {/* )} */}
            {!isLegManagementEnabled(order) && (
              <ShipmentsList
                order={order}
                bookedShipments={bookedShipments}
                stagingShipments={stagingShipments}
                renderedInModal={renderedInModal}
                onGetPaginatedShipments={this.handleShipmentListPagination}
                paginationData={this.state.shipmentListPagination}
              />
            )}
            {/* Order RTB Summary above AR/AP section (only for LMV === 2) */}
            {isLegManagementEnabled(order) && (
              <div className="my-6">
                <OrderRtbSummary
                  orderId={order?._id}
                  order={order}
                  shipments={bookedShipments}
                />
              </div>
            )}
            {!isLegManagementEnabled(order) && (
              <div
                data-testid="div-99fac929"
                className="my-6 grid gap-y-6 sm:grid-cols-2 sm:gap-x-4"
              >
                <div
                  data-testid="div-1067acea"
                  className="sm:col-span-1 min-w-0"
                >
                  <AccountsReceivable
                    order={order}
                    shipments={bookedShipments}
                  />
                </div>
                <div
                  data-testid="div-d143ceed"
                  className="sm:col-span-1 min-w-0"
                >
                  <AccountsPayable order={order} shipments={bookedShipments} />
                </div>
                {this.state.hasFinanceRole && (
                  <div
                    data-testid="div-c12fb1a8"
                    className="sm:col-span-2 min-w-0"
                  >
                    <AccessorialsList
                      order={order}
                      shipments={bookedShipments}
                      onAddBulkAccessorials={
                        this.handleAddBulkAccessorialsClick
                      }
                    />
                  </div>
                )}
              </div>
            )}
            <div className="my-6">
              <CargomaticCollapsibleCard
                id="order-audit-history"
                header={() => <span>{t('AUDIT_HISTORY')}</span>}
              >
                <AuditHistory
                  audits={this.state.audits}
                  currentPage={this.state.auditPage}
                  totalCount={this.state.audits?.totalCount}
                  isLoading={this.state.auditLoading}
                  onSearch={(search) => {
                    this.setState({ auditSearch: search, auditPage: 1 }, () =>
                      this.fetchOrderAudits(),
                    );
                  }}
                  onPageChange={(page) => {
                    this.setState({ auditPage: page }, () =>
                      this.fetchOrderAudits(),
                    );
                  }}
                />
              </CargomaticCollapsibleCard>
            </div>
          </div>
        )}
        {order?.reference_numbers && (
          <EditReferenceNumbersModal
            reference_numbers={order.reference_numbers}
            shipper={order.shipper}
            isIntermodalShipmentType
            page="Orders"
            show={this.state.showEditReferenceNumbersModal}
            onSave={(references) => this.updateReferenceNumbers(references)}
            onClose={() => {
              this.setState({ showEditReferenceNumbersModal: false });
            }}
          />
        )}
        {this.state.showArrivedModal && (
          <ArrivedModal
            dataTestId="ArrivedModal-c00242a0"
            show={this.state.showArrivedModal}
            onComplete={(workflow, meta) => {
              OrdersActionCreators.transitionOrder(order, workflow, meta);
            }}
            onHide={() => this.setState({ showArrivedModal: false })}
          />
        )}
        {!isLegManagementEnabled(order) &&
          this.state.showBulkAddAccessorialsModal && (
            <BulkAddAccessorialsModal
              show={this.state.showBulkAddAccessorialsModal}
              orderId={this.getOrder()._id}
              shipmentIds={this.getShipmentsEligibleForAccessorialsManagement().map(
                (s) => s._id,
              )}
              onSave={this.handleBulkCreateAccessorials}
              onHide={() => {
                this.setState({ showBulkAddAccessorialsModal: false });
              }}
              edit={false}
            />
          )}
        <div data-testid="div-1d32f7ce" style={{ height: 400 }}>
          &nbsp;
        </div>
      </div>
    );
  }
}

Module.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  renderedInModal: PropTypes.bool,
};

export default withTranslation()(Module);
